import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  handleSpringDataMutationRequest,
  handleSpringDataRequest,
} from "../../../../utils/apiUtils";

function MultiChannelMappingContent({ hotelId }) {
  const [multiMappingData, setMultiMappingData] = useState([]);
  const [multiMappingLoading, setMultiMappingLoading] = useState(false);
  const [addMultiMappingLoading, setAddMultiMappingLoading] = useState(false);
  const [deleteMultiMappingLoadingKey, setDeleteMultiMappingLoadingKey] =
    useState("");
  const [multiMappingFormData, setMultiMappingFormData] = useState({
    channel_hotel_id: "",
    channel_roomType_id: "",
    hotel_id: "",
    roomType_id: "",
  });

  const normalizeMappingItem = (item = {}) => ({
    id: item?.id,
    channel_hotel_id: item?.channel_hotel_id || item?.channelHotelId || "",
    channel_roomType_id:
      item?.channel_roomType_id || item?.channelRoomTypeId || "",
    hotel_id: item?.hotel_id || item?.hotelId || "",
    roomType_id: item?.roomType_id || item?.roomTypeId || "",
  });

  const fetchMultiMappingData = () => {
    setMultiMappingLoading(true);
    handleSpringDataRequest(
      `metaAdmin/api/v1/get-channel-multi-hotel-mapping?hotelId=${hotelId}`
    )
      .then((res) => {
        const rows = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : [];
        if (Array.isArray(res)) {
          setMultiMappingData(rows.map(normalizeMappingItem));
        } else if (Array.isArray(res?.data)) {
          setMultiMappingData(rows.map(normalizeMappingItem));
        } else {
          setMultiMappingData([]);
        }
      })
      .catch(() => {
        setMultiMappingData([]);
        toast.error("Failed to fetch multi channel mappings");
      })
      .finally(() => {
        setMultiMappingLoading(false);
      });
  };

  useEffect(() => {
    fetchMultiMappingData();
  }, [hotelId]);

  const handleMultiMappingInputChange = (field, value) => {
    setMultiMappingFormData((prev) => ({
      ...prev,
      [field]: value.trim(),
    }));
  };

  const handleAddMultiMapping = () => {
    const hasEmptyField = Object.values(multiMappingFormData).some(
      (value) => !value
    );
    if (hasEmptyField) {
      toast.error("All fields are required for multi mapping");
      return;
    }

    setAddMultiMappingLoading(true);
    handleSpringDataMutationRequest(
      "POST",
      `metaAdmin/api/v1/add-channel-multi-hotel-mapping?hotelId=${hotelId}`,
      multiMappingFormData
    )
      .then(() => {
        toast.success("Multi channel mapping added");
        setMultiMappingFormData({
          channel_hotel_id: "",
          channel_roomType_id: "",
          hotel_id: "",
          roomType_id: "",
        });
        fetchMultiMappingData();
      })
      .catch(() => {
        toast.error("Failed to add multi channel mapping");
      })
      .finally(() => {
        setAddMultiMappingLoading(false);
      });
  };

  const getDeleteMultiMappingUrl = (item) => {
    if (item?.id) {
      return `metaAdmin/api/v1/delete-channel-multi-hotel-mapping?hotelId=${hotelId}&id=${item.id}`;
    }
    return `metaAdmin/api/v1/delete-channel-multi-hotel-mapping?hotelId=${hotelId}&channelHotelId=${item?.channel_hotel_id}&channelRoomTypeId=${item?.channel_roomType_id}`;
  };

  const handleDeleteMultiMapping = (item) => {
    const loadingKey = item?.id
      ? `id-${item.id}`
      : `${item?.channel_hotel_id}-${item?.channel_roomType_id}`;
    setDeleteMultiMappingLoadingKey(loadingKey);
    handleSpringDataMutationRequest("DELETE", getDeleteMultiMappingUrl(item))
      .then(() => {
        toast.success("Multi channel mapping deleted");
        fetchMultiMappingData();
      })
      .catch(() => {
        toast.error("Failed to delete multi channel mapping");
      })
      .finally(() => {
        setDeleteMultiMappingLoadingKey("");
      });
  };

  return (
    <>
      <Typography variant="h6" sx={{ ml: 2, mb: 2, mt: 2 }}>
        Channel multi hotel mapping
      </Typography>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ px: 2, pb: 2 }}
        flexWrap="wrap"
      >
        <TextField
          size="small"
          label="Channel Hotel Id"
          value={multiMappingFormData.channel_hotel_id}
          onChange={(e) =>
            handleMultiMappingInputChange("channel_hotel_id", e.target.value)
          }
        />
        <TextField
          size="small"
          label="Channel RoomType Id"
          value={multiMappingFormData.channel_roomType_id}
          onChange={(e) =>
            handleMultiMappingInputChange(
              "channel_roomType_id",
              e.target.value
            )
          }
        />
        <TextField
          size="small"
          label="Hotel Id"
          value={multiMappingFormData.hotel_id}
          onChange={(e) =>
            handleMultiMappingInputChange("hotel_id", e.target.value)
          }
        />
        <TextField
          size="small"
          label="RoomType Id"
          value={multiMappingFormData.roomType_id}
          onChange={(e) =>
            handleMultiMappingInputChange("roomType_id", e.target.value)
          }
        />
        <Button
          variant="custom-button"
          onClick={handleAddMultiMapping}
          disabled={addMultiMappingLoading}
        >
          Add mapping
          {addMultiMappingLoading && (
            <CircularProgress size={15} color="inherit" sx={{ ml: 1 }} />
          )}
        </Button>
      </Stack>
      <Table size="small" sx={{ mb: 2, px: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Channel Hotel Id</TableCell>
            <TableCell>Channel RoomType Id</TableCell>
            <TableCell>Hotel Id</TableCell>
            <TableCell>RoomType Id</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {multiMappingData?.map((item, idx) => {
            const loadingKey = item?.id
              ? `id-${item.id}`
              : `${item?.channel_hotel_id}-${item?.channel_roomType_id}`;
            return (
              <TableRow key={item?.id || `${loadingKey}-${idx}`}>
                <TableCell>{item?.channel_hotel_id || "-"}</TableCell>
                <TableCell>{item?.channel_roomType_id || "-"}</TableCell>
                <TableCell>{item?.hotel_id || "-"}</TableCell>
                <TableCell>{item?.roomType_id || "-"}</TableCell>
                <TableCell align="right">
                  <Button
                    color="error"
                    size="small"
                    onClick={() => handleDeleteMultiMapping(item)}
                    disabled={deleteMultiMappingLoadingKey === loadingKey}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {!multiMappingLoading && !multiMappingData?.length && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                No multi mappings found
              </TableCell>
            </TableRow>
          )}
          {multiMappingLoading && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <CircularProgress size={20} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
}

const MultiChannelMappingModal = ({ show, hide, hotelId }) => {
  return (
    <Modal
      open={show}
      aria-labelledby="multi-channel-mapping-title"
      aria-describedby="multi-channel-mapping-description"
    >
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          height: "100%",
          width: "70%",
          backgroundColor: "#ffffff",
          overflow: "auto",
        }}
      >
        <span
          style={{
            position: "absolute",
            left: "16px",
            top: "16px",
            cursor: "pointer",
          }}
          onClick={hide}
        >
          X
        </span>
        <Divider sx={{ mt: 6 }} />
        {hotelId ? (
          <MultiChannelMappingContent hotelId={hotelId} />
        ) : null}
      </Box>
    </Modal>
  );
};

export default MultiChannelMappingModal;
